/**
 * FastAPI `detail` → user-facing message.
 *
 * - JSON APIs (sync): `getUserFacingApiMessage(error, fallback)`
 * - Blob `responseType` (async): `getUserFacingApiMessageAsync(error, fallback)`
 * - Business message or null (general toasts): `getBusinessErrorMessage(error)`
 * - 4xx + field errors: `parse4xxApiError(error)`
 *
 * Lower-level: `getResponseDetail` / `getResponseDetailFromBlobOrJson` + `extractDetailMessage`.
 * Never surface `error.message`, status text, or raw non-JSON blob/proxy bodies.
 * When the request never reached the server (offline, DNS, CORS, timeout) the
 * user-facing helpers return a generic connectivity hint instead of the caller's
 * fallback, so a network outage doesn't read like a server-side failure.
 */

import { getHttpStatus } from './httpErrorUtils';

export interface FieldValidationError {
  field: string;
  message: string;
}

interface StructuredErrorDetail {
  message?: string;
  errors?: unknown[];
}

function resolveErrorBody(error: unknown): unknown {
  if (!error || typeof error !== 'object') return error;
  const wrapped = error as { response?: { data?: unknown }; error?: unknown };
  if (wrapped.response?.data !== undefined) return wrapped.response.data;
  if (wrapped.error !== undefined) return wrapped.error;
  return error;
}

function extractDetailFromBody(body: unknown): unknown {
  if (body && typeof body === 'object' && 'detail' in body) {
    return (body as { detail?: unknown }).detail;
  }
  return undefined;
}

/** Raw FastAPI `detail` from a JSON API error. Use {@link getResponseDetailFromBlobOrJson} for blobs. */
export function getResponseDetail(error: unknown): unknown {
  return extractDetailFromBody(resolveErrorBody(error));
}

/**
 * True when a payer exit refused a test-patient submission pending acknowledgement
 * (ROG-2900). The backend returns 409 with `detail.code === 'test_patient_ack_required'`
 * so the caller can warn and re-submit with `acknowledge_test_patient: true`.
 * Accepts either a thrown axios error or a hey-api `{ error, response }` result.
 */
export function isTestPatientAckRequired(error: unknown): boolean {
  if (getHttpStatus(error) !== 409) return false;
  const detail = getResponseDetail(error);
  return (
    !!detail &&
    typeof detail === 'object' &&
    (detail as { code?: unknown }).code === 'test_patient_ack_required'
  );
}

/** User-facing message from a `detail` value (string, `{ message }`, or Pydantic 422 array). */
export function extractDetailMessage(detail: unknown): string | null {
  if (typeof detail === 'string') {
    const trimmed = detail.trim();
    return trimmed || null;
  }
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const msg = (detail as StructuredErrorDetail).message;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  return null;
}

function formatPydanticValidationDetail(detail: unknown): string | null {
  if (!Array.isArray(detail)) return null;
  const parts = detail
    .filter(
      (item): item is { msg?: string; loc?: (string | number)[] } =>
        item != null && typeof item === 'object'
    )
    .map(item => {
      const field =
        item.loc?.filter(s => typeof s === 'string').join('.') || 'Field';
      return `${field}: ${item.msg || 'Invalid value'}`;
    });
  return parts.length > 0 ? parts.join('; ') : null;
}

function toUserFacingMessage(detail: unknown): string | null {
  return extractDetailMessage(detail) ?? formatPydanticValidationDetail(detail);
}

const NETWORK_ERROR_MESSAGE =
  'Network error. Please check your connection and try again.';

/**
 * Messages browsers use when `fetch` rejects for a connectivity failure. The
 * Fetch spec throws a bare `TypeError`, so we match on the message to avoid
 * mislabeling an unrelated `TypeError` (a real code bug) as an outage.
 * - Chrome/Edge: "Failed to fetch"  - Firefox: "NetworkError when attempting…"
 * - Safari: "Load failed" / "The network connection was lost." / "…offline."
 * - Node/undici (test env): "fetch failed"
 */
const FETCH_NETWORK_ERROR_PATTERN =
  /failed to fetch|networkerror|fetch failed|load failed|network connection|connection appears to be offline/i;

/**
 * True when the request never received an HTTP response (offline, DNS, CORS,
 * timeout) rather than the server returning an error body. Lets the user-facing
 * helpers show a connectivity hint without surfacing the raw `error.message`.
 */
function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  // A response came back (any status) -> not a connectivity failure.
  if (getHttpStatus(error) !== undefined) return false;
  const e = error as {
    code?: unknown;
    message?: unknown;
    request?: unknown;
    response?: unknown;
  };
  // axios: connection failure / timeout, or a request sent with no response.
  if (e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED') return true;
  if (e.request != null && e.response == null) return true;
  // fetch (hey-api) rejects network failures with a TypeError; gate on the
  // known per-browser messages so unrelated TypeErrors fall through.
  return (
    error instanceof TypeError &&
    typeof e.message === 'string' &&
    FETCH_NETWORK_ERROR_PATTERN.test(e.message)
  );
}

/**
 * 5xx (and proxy) responses must never surface their body — that text is for
 * operators, not users, and "5xx → generic copy" is the documented contract.
 * Only 4xx business messages and validation details are safe to show. When the
 * status is unknown (a bare body was passed, or a non-HTTP failure) we still
 * prefer a usable detail so callers that cannot supply the full response keep
 * working, then fall back to a connectivity hint or the caller's generic copy.
 *
 * Prefer passing the full error/response (an axios error or a hey-api
 * `{ error, response }` result) rather than a bare body so this status gate can
 * engage; a missing status degrades gracefully rather than throwing.
 */
function resolveUserFacingMessage(
  detail: unknown,
  error: unknown,
  fallback: string
): string {
  const status = getHttpStatus(error);
  const serverMessage =
    status !== undefined && status >= 500 ? null : toUserFacingMessage(detail);
  return (
    serverMessage ?? (isNetworkError(error) ? NETWORK_ERROR_MESSAGE : fallback)
  );
}

/** Sync user-facing message from an API error, with a generic fallback. */
export function getUserFacingApiMessage(
  error: unknown,
  fallback: string
): string {
  return resolveUserFacingMessage(getResponseDetail(error), error, fallback);
}

/**
 * Server **business** message safe to surface in a toast, or `null`. The
 * status-aware replacement for the raw `extractDetailMessage(getResponseDetail(
 * error))` pattern: returns the string / `{ message }` detail only for 4xx (and
 * status-unknown) responses, and `null` for 5xx / proxy bodies and Pydantic 422
 * arrays — so 5xx detail strings and backend `body.<field>` paths never leak.
 * Callers supply their own generic fallback (`?? 'Failed to …'`); pass the full
 * error/response so the status gate can engage.
 */
export function getBusinessErrorMessage(error: unknown): string | null {
  const status = getHttpStatus(error);
  if (status !== undefined && status >= 500) return null;
  return extractDetailMessage(getResponseDetail(error));
}

export function parseFieldErrorsFromDetail(
  detail: unknown
): FieldValidationError[] | undefined {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
    return undefined;
  }
  const errors = (detail as StructuredErrorDetail).errors;
  if (!Array.isArray(errors)) return undefined;
  return errors.filter(
    (e): e is FieldValidationError =>
      e != null &&
      typeof e === 'object' &&
      typeof (e as FieldValidationError).field === 'string' &&
      typeof (e as FieldValidationError).message === 'string'
  );
}

export interface SurfaceableError {
  errorCode: string;
  message: string;
  data: Record<string, unknown>;
}

/**
 * Optional fields a hook result carries when the server flagged a surfaceable
 * business error. The `surfaceable` flag is the trust boundary that lets the UI
 * show a server-provided message verbatim (see CLAUDE.md error-handling rule).
 */
export interface SurfaceableResultFields {
  surfaceable?: boolean;
  error_code?: string;
}

/**
 * Returns the surfaceable business error iff the server flagged it; else null.
 *
 * Works for both error shapes the hooks encounter: a thrown AxiosError
 * (`error.response.data.detail`) and a hey-api result (`result.error.detail`).
 * Only `surfaceable === true` payloads with an `error_code` + `message` pass —
 * everything else returns null so generic copy is used.
 */
export function getSurfaceableError(
  errorOrResponse: unknown
): SurfaceableError | null {
  const detail =
    getResponseDetail(errorOrResponse) ??
    (errorOrResponse as { error?: { detail?: unknown } })?.error?.detail;
  if (
    detail &&
    typeof detail === 'object' &&
    !Array.isArray(detail) &&
    (detail as { surfaceable?: unknown }).surfaceable === true &&
    typeof (detail as { error_code?: unknown }).error_code === 'string' &&
    typeof (detail as { message?: unknown }).message === 'string'
  ) {
    const d = detail as {
      error_code: string;
      message: string;
      data?: Record<string, unknown>;
    };
    return { errorCode: d.error_code, message: d.message, data: d.data ?? {} };
  }
  return null;
}

export function parse4xxDetail(detail: unknown): {
  message: string | null;
  fieldErrors?: FieldValidationError[];
} {
  const fieldErrors = parseFieldErrorsFromDetail(detail);
  return {
    message: extractDetailMessage(detail),
    ...(fieldErrors && fieldErrors.length > 0 ? { fieldErrors } : {}),
  };
}

export function parse4xxApiError(error: unknown): {
  message: string | null;
  fieldErrors?: FieldValidationError[];
} {
  const status = getHttpStatus(error);
  if (status === undefined || status < 400 || status >= 500) {
    return { message: null };
  }
  return parse4xxDetail(getResponseDetail(error));
}

/** Raw `detail` when the body may be a Blob (async). */
export async function getResponseDetailFromBlobOrJson(
  error: unknown
): Promise<unknown> {
  let body = resolveErrorBody(error);
  if (body instanceof Blob) {
    try {
      body = JSON.parse(await new Response(body).text());
    } catch {
      return undefined;
    }
  }
  // Parity with the sync getResponseDetail: only read a JSON `detail`, never a
  // raw plain-string / proxy body (those must fall through to generic copy).
  return extractDetailFromBody(body);
}

export async function getUserFacingApiMessageAsync(
  error: unknown,
  fallback: string
): Promise<string> {
  return resolveUserFacingMessage(
    await getResponseDetailFromBlobOrJson(error),
    error,
    fallback
  );
}

/**
 * Same as `parse4xxApiError`, for a hey-api result — the non-throwing client
 * resolves HTTP failures into `{ error, response }` instead of rejecting.
 */
export function parse4xxHeyApiResult(result: {
  error?: unknown;
  response?: { status?: number };
  status?: number;
}): {
  message: string | null;
  fieldErrors?: FieldValidationError[];
} {
  return parse4xxApiError({
    response: {
      status: result.response?.status ?? result.status,
      data: result.error,
    },
  });
}
